import styled, { css } from 'styled-components'

import {
  ButtonUnstyled,
  FlatButton,
  Input,
  InputWrapper,
  Text,
} from '@sketch/components'

import { ReactComponent as WorkspaceIconSvg } from '@sketch/icons/workspace-17'
import { ReactComponent as ClockwiseArrowCircle } from '@sketch/icons/arrow-clockwise-17'
import { ReactComponent as ChevronsUpDownSvg } from '@sketch/icons/chevrons-up-down-16'

export const Wrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  gap: 20px;
  padding: 17px 20px 20px;
  overflow-y: auto;
`

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const SectionHeader = styled(Text.H3).attrs({
  textStyle: 'copy.tertiary.standard.D',
})(
  ({ theme }) => css`
    margin: 0;
    color: ${theme.colors.foreground.secondary.B};
    font-weight: 500;
  `
)

export const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

export const WorkspaceTrigger = styled(ButtonUnstyled)(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 32px;
    box-sizing: border-box;
    border-radius: ${theme.radii.large};
    padding: 6px 12px;
    background-color: ${theme.colors.background.tertiary.C};
    border: none;
    cursor: pointer;
    font-size: ${theme.fontSizes.D};
    color: ${theme.colors.foreground.secondary.A};
    text-align: left;
  `
)

export const WorkspaceTriggerContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const TriggerChevron = styled(ChevronsUpDownSvg)(
  ({ theme }) => css`
    width: 16px;
    height: 16px;
    color: ${theme.colors.foreground.secondary.D};
    flex-shrink: 0;
  `
)

export const WorkspaceMenu = styled.div`
  min-width: 240px;
  padding: 4px 0;
`

export const WorkspaceOptionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const WorkspaceIcon = styled(WorkspaceIconSvg)(
  ({ theme }) => css`
    width: 16px;
    height: 16px;
    color: ${theme.colors.foreground.secondary.B};
  `
)

export const DateInputs = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: ${theme.colors.foreground.secondary.C};

    ${InputWrapper} {
      width: 100%;
    }
  `
)

export const DatePicker = styled(Input).attrs({ type: 'date' })<{
  isDarkMode: boolean
}>(
  ({ theme, isDarkMode }) => css`
    width: 100%;
    height: 32px;
    box-sizing: border-box;
    border-radius: ${theme.radii.large};
    border: none;
    padding: 6px 12px;
    background-color: ${theme.colors.background.tertiary.C};
    color: ${theme.colors.foreground.secondary.A};
    margin: 0;
    font-size: ${theme.fontSizes.D};

    ::-webkit-calendar-picker-indicator {
      filter: ${isDarkMode ? 'invert(50%)' : undefined};
    }

    ::-webkit-datetime-edit {
      color: ${theme.colors.foreground.secondary.D};
      opacity: 1; /* Override Firefox default */
    }

    ::-webkit-calendar-picker-indicator {
      opacity: 0.55;
    }

    &:not([value='']) {
      ::-webkit-datetime-edit {
        color: ${theme.colors.foreground.secondary.A};
        opacity: 1; /* Override Firefox default */
      }

      ::-webkit-calendar-picker-indicator {
        opacity: 1;
      }
    }
  `
)

export const StyledClockwiseArrowCircle = styled(ClockwiseArrowCircle)`
  width: 12px;
  height: 12px;
`

export const ResetButton = styled(FlatButton)(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    font-size: ${theme.fontSizes.D};

    padding: 12px 0;
    display: flex;
    align-items: center;
    cursor: pointer;

    // Prevent the button to grow right
    margin-right: auto;

    &[disabled] {
      cursor: default;
      opacity: 0.5;
    }

    ${StyledClockwiseArrowCircle} {
      margin-right: 8px;
    }
  `
)
